import { LANDMARKS, landmarkById, npcById, type LandmarkId, type NpcId } from "./catalog";
import { setMuted, type SaveState } from "./save";

function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
}

type CollectItem = { kind: "postcard"; id: LandmarkId } | { kind: "badge"; id: NpcId };

export class GameUI {
  readonly gate = must<HTMLElement>("gate");
  readonly enter = must<HTMLButtonElement>("btn-enter");
  readonly hud = must<HTMLElement>("hud");
  readonly hint = must<HTMLParagraphElement>("hint");
  readonly interactBtn = must<HTMLButtonElement>("btn-interact");
  readonly enterSiteBtn = must<HTMLButtonElement>("btn-enter-site");
  readonly muteBtn = must<HTMLButtonElement>("btn-mute");
  readonly zoomIn = must<HTMLButtonElement>("btn-zoom-in");
  readonly zoomOut = must<HTMLButtonElement>("btn-zoom-out");
  readonly homeBtn = must<HTMLButtonElement>("btn-home");
  readonly helpBtn = must<HTMLButtonElement>("btn-help");
  readonly notes = must<HTMLElement>("notes");
  readonly pocketCount = must<HTMLElement>("pocket-count");
  readonly pocketSlots = must<HTMLUListElement>("pocket-slots");
  readonly talkDialog = must<HTMLDialogElement>("talk-dialog");
  readonly talkName = must<HTMLHeadingElement>("talk-name");
  readonly talkLine = must<HTMLParagraphElement>("talk-line");
  readonly talkKicker = must<HTMLParagraphElement>("talk-kicker");
  readonly talkNext = must<HTMLButtonElement>("talk-next");
  readonly talkGuide = must<HTMLButtonElement>("talk-guide");
  readonly collectDialog = must<HTMLDialogElement>("collect-dialog");
  readonly collectKicker = must<HTMLParagraphElement>("collect-kicker");
  readonly collectTitle = must<HTMLHeadingElement>("collect-title");
  readonly collectBody = must<HTMLParagraphElement>("collect-body");
  readonly collectArt = must<HTMLElement>("collect-art");
  readonly collectOpen = must<HTMLButtonElement>("collect-open");
  readonly helpDialog = must<HTMLDialogElement>("help-dialog");

  private save: SaveState;
  private queue: CollectItem[] = [];
  private talkNpc: NpcId | null = null;
  private hintNpc: NpcId | null = null;
  private hintLandmark: LandmarkId | null = null;
  private talkIndex = 0;
  private pendingUrl = "";

  onEnter: () => void = () => {};
  onGuide: (id: NpcId) => void = () => {};
  onTalk: (id: NpcId) => void = () => {};
  onMute: (muted: boolean) => void = () => {};
  onOpenSite: (url: string) => void = () => {};
  onZoom: (dir: number) => void = () => {};
  onHome: () => void = () => {};
  onLabelClick: (kind: "landmark" | "npc", id: string) => void = () => {};

  constructor(save: SaveState) {
    this.save = save;
    this.enter.addEventListener("click", () => this.onEnter());
    this.muteBtn.addEventListener("click", () => this.toggleMute());
    this.zoomIn.addEventListener("click", () => this.onZoom(-1));
    this.zoomOut.addEventListener("click", () => this.onZoom(1));
    this.homeBtn.addEventListener("click", () => this.onHome());
    this.helpBtn.addEventListener("click", () => this.helpDialog.showModal());
    this.interactBtn.addEventListener("click", () => {
      if (this.hintNpc) this.onTalk(this.hintNpc);
    });
    this.enterSiteBtn.addEventListener("click", () => {
      if (this.hintLandmark) this.onLabelClick("landmark", this.hintLandmark);
    });
    this.talkNext.addEventListener("click", () => this.advanceTalk());
    this.talkGuide.addEventListener("click", () => {
      if (!this.talkNpc) return;
      const id = this.talkNpc;
      this.talkDialog.close();
      this.onGuide(id);
    });
    this.talkDialog.addEventListener("close", () => {
      this.talkNpc = null;
    });
    this.collectOpen.addEventListener("click", () => {
      if (this.pendingUrl) this.onOpenSite(this.pendingUrl);
      this.collectDialog.close();
    });
    this.collectDialog.addEventListener("close", () => this.flushQueue());
    this.renderMute();
    this.renderProgress();
    this.renderPocket();
    this.enter.focus();
  }

  get blocking(): boolean {
    return !this.gate.hidden || this.talkDialog.open || this.collectDialog.open || this.helpDialog.open;
  }

  startPlay(): void {
    this.gate.hidden = true;
    this.hud.hidden = false;
  }

  setHint(text: string | null, interact: NpcId | null, landmark: LandmarkId | null = null): void {
    this.hintNpc = interact;
    this.hintLandmark = landmark;
    if (!text) {
      this.hint.hidden = true;
      this.interactBtn.hidden = true;
      this.enterSiteBtn.hidden = true;
      return;
    }
    this.hint.hidden = false;
    this.hint.textContent = text;
    this.interactBtn.hidden = !interact;
    this.enterSiteBtn.hidden = !landmark;
    if (landmark) {
      const def = landmarkById(landmark);
      this.enterSiteBtn.textContent = def.url ? `进入${def.name}` : "看一看";
    }
  }

  openTalk(id: NpcId): void {
    const npc = npcById(id);
    this.talkNpc = id;
    this.talkIndex = 0;
    this.talkKicker.textContent = npc.title;
    this.talkName.textContent = npc.name;
    this.talkLine.textContent = npc.lines[0] ?? "";
    this.talkNext.hidden = npc.lines.length < 2;
    this.talkDialog.showModal();
  }

  showCollect(item: CollectItem): void {
    if (this.collectDialog.open || this.talkDialog.open) {
      this.queue.push(item);
      return;
    }
    if (item.kind === "postcard") {
      const def = landmarkById(item.id);
      this.collectKicker.textContent = def.url ? "小屋明信片" : "风景明信片";
      this.collectTitle.textContent = def.postcardTitle;
      this.collectBody.textContent = def.postcardBody;
      this.collectArt.innerHTML = `<div class="art-card" style="--accent:#${def.accent.toString(16).padStart(6, "0")}"></div>`;
      this.pendingUrl = def.url;
      this.collectOpen.textContent = `打开${def.name}`;
    } else {
      const def = npcById(item.id);
      const target = landmarkById(def.guideTo);
      this.collectKicker.textContent = "精灵徽章";
      this.collectTitle.textContent = def.badgeTitle;
      this.collectBody.textContent = def.badgeBody;
      this.collectArt.innerHTML = `<div class="art-badge ${item.id}"></div>`;
      this.pendingUrl = target.url;
      this.collectOpen.textContent = `打开${target.name}`;
    }
    // 风景小屋没有站点，不给假门
    this.collectOpen.hidden = !this.pendingUrl;
    this.collectDialog.showModal();
    this.renderPocket();
    this.renderProgress();
  }

  refresh(save: SaveState): void {
    this.save = save;
    this.renderProgress();
    this.renderPocket();
    this.renderMute();
  }

  toggleMute(): void {
    setMuted(this.save, !this.save.muted);
    this.renderMute();
    this.onMute(this.save.muted);
  }

  closeModals(): void {
    if (this.talkDialog.open) this.talkDialog.close();
    if (this.collectDialog.open) this.collectDialog.close();
    if (this.helpDialog.open) this.helpDialog.close();
  }

  private advanceTalk(): void {
    if (!this.talkNpc) return;
    const lines = npcById(this.talkNpc).lines;
    this.talkIndex = Math.min(this.talkIndex + 1, lines.length - 1);
    this.talkLine.textContent = lines[this.talkIndex] ?? "";
    if (this.talkIndex >= lines.length - 1) this.talkNext.hidden = true;
  }

  private flushQueue(): void {
    const next = this.queue.shift();
    if (next) window.setTimeout(() => this.showCollect(next), 120);
  }

  private renderMute(): void {
    this.muteBtn.setAttribute("aria-pressed", this.save.muted ? "true" : "false");
    this.muteBtn.textContent = this.save.muted ? "音乐关" : "音乐开";
  }

  private renderProgress(): void {
    const n = this.save.postcards.length;
    const total = LANDMARKS.length;
    this.notes.textContent = `小屋 ${n}/${total}`;
    this.pocketCount.textContent = `${n}/${total}`;
  }

  private renderPocket(): void {
    this.pocketSlots.replaceChildren();
    for (const def of LANDMARKS) {
      const li = document.createElement("li");
      const unlocked = this.save.postcards.includes(def.id);
      li.className = unlocked ? "slot on" : "slot";
      li.title = unlocked ? def.name : "还没走到";
      if (unlocked) {
        li.style.setProperty("--c", `#${def.accent.toString(16).padStart(6, "0")}`);
        if (def.url) {
          li.setAttribute("role", "button");
          li.tabIndex = 0;
          li.setAttribute("aria-label", `打开${def.name}`);
          li.addEventListener("click", () => this.onOpenSite(def.url));
          li.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              this.onOpenSite(def.url);
            }
          });
        } else {
          li.classList.add("scenic");
        }
      }
      this.pocketSlots.append(li);
    }
  }
}
